import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';
import { isTrailerRenter, hasValidUserType } from '../util/genericHelpers';
import { useConfiguration } from '../context/configurationContext';

import { locationChanged } from '../ducks/routing.duck';

import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';

const isBanned = currentUser => {
  const isBrowser = typeof window !== 'undefined';
  // Future todo: currentUser?.attributes?.state === 'banned'
  return isBrowser && currentUser?.attributes?.banned === true;
};

const canShowComponent = props => {
  const { isAuthenticated, currentUser, route } = props;
  const { auth } = route;
  return !auth || (isAuthenticated && !isBanned(currentUser));
};

// Owner-only route guard (PL2). Some routes are for trailer OWNERS only
// (create/edit/manage listings, owner dashboard). We only BLOCK a user once
// currentUser is fully resolved AND they are an EXPLICITLY-tagged renter.
// While currentUser is still loading we do NOT block (auth:true already forces
// login); owners + untagged + junk-tagged users are always allowed through.
// This mirrors the safe default used in the topbar hide logic.
const isBlockedByOwnerOnly = props => {
  const { currentUser, route } = props;
  if (!route?.ownerOnly) return false;
  const hasCurrentUser = !!currentUser?.id;
  return hasCurrentUser && isTrailerRenter(currentUser);
};

// Onboarding guard (2026-08-07): custom onboarding is the ONLY path a new user
// sees. Any AUTHENTICATED user whose currentUser is fully resolved but who has
// NO valid onboarding role (never chose, or a polluted "business-name" value)
// is redirected into /onboarding, from wherever they land. This is the backstop
// for sessions that don't pass through AuthenticationPage (e.g. the server-side
// IdP redirect drops new Google/FB users straight onto `/`).
//
// Route names where we must NOT force onboarding, or we'd loop / trap the user:
//  - OnboardingPage itself (the destination)
//  - auth + account-recovery flows (login/signup/confirm/verify/password)
//  - logout
//  - legal pages the onboarding UI links to (Terms / Privacy) so they stay reachable
//  - Stripe payout return pages: the owner onboarding step leaves to Stripe and
//    returns here; forcing onboarding would fight that round-trip.
const ONBOARDING_EXEMPT_ROUTE_NAMES = [
  'OnboardingPage',
  'LoginPage',
  'SignupPage',
  'SignupForUserTypePage',
  'ConfirmPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'PasswordRecoveryPage',
  'PasswordChangePage',
  'TermsOfServicePage',
  'PrivacyPolicyPage',
  'StripePayoutPage',
  'StripePayoutOnboardingPage',
];

const isBlockedByOnboarding = props => {
  const { currentUser, isAuthenticated, route, logoutInProgress, saveRoleInProgress } = props;
  if (!isAuthenticated || logoutInProgress) return false;
  // Don't fight an in-flight role persist. When the user has just picked a role
  // in the RoleChooser, saveRoleAndState is mid-PATCH; the Redux currentUser may
  // still be stale for a beat. Blocking here would bounce them back to
  // /onboarding right as they click a CTA (renter Browse / owner checkout). The
  // thunk dispatches currentUserShowSuccess before flipping this false, so once
  // it clears the role is already in currentUser. (Belt-and-braces backstop for
  // the CTA gating done in OnboardingPage itself.)
  if (saveRoleInProgress) return false;
  // Only act once currentUser is fully resolved; while loading we never block
  // (auth:true already gates login; avoids SSR/hydration flicker + false redirects).
  const hasCurrentUser = !!currentUser?.id;
  if (!hasCurrentUser) return false;
  if (isBanned(currentUser)) return false;
  if (ONBOARDING_EXEMPT_ROUTE_NAMES.includes(route?.name)) return false;
  return !hasValidUserType(currentUser);
};

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' &&
    canShowComponent(props) &&
    !isBlockedByOwnerOnly(props) &&
    !isBlockedByOnboarding(props) &&
    !logoutInProgress;

  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search, config))
      .then(() => {
        if (props.logLoadDataCalls) {
          // This gives good input for debugging issues on live environments, but with test it's not needed.
          console.log(`loadData success for ${name} route`);
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

const setPageScrollPosition = (location, delayed) => {
  if (!location.hash) {
    // No hash, scroll to top
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.

      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView
      // Note: 300 milliseconds might not be enough, but adding too much delay
      // might affect user initiated scrolling.
      delayed = window.setTimeout(() => {
        const reTry = document.querySelector(location.hash);
        reTry?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }, 300);
    }
  }
};

const handleLocationChanged = (dispatch, location, routeConfiguration, delayed) => {
  setPageScrollPosition(location, delayed);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, routeConfiguration } = this.props;
    this.delayed = null;
    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      // Calling loadData after initial rendering (on client side).
      // This makes it possible to use loadData as default client side data loading technique.
      // However it is better to fetch data before location change to avoid "Loading data" state.
      callLoadData(this.props);
      handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
    }
  }

  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.resetTimeoutId);
    }
  }

  render() {
    const { route, match, location, staticContext, currentUser } = this.props;
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    // PL2: owner-only routes are blocked for confirmed renters (once resolved).
    const blockedByOwnerOnly = isBlockedByOwnerOnly(this.props);
    // 2026-08-07: authed users with no valid onboarding role are forced into
    // /onboarding (backstop for the server-side IdP redirect that lands new
    // social users on `/`). Exempt routes above prevent loops.
    const blockedByOnboarding = isBlockedByOnboarding(this.props);
    if (!canShow || blockedByOwnerOnly || blockedByOnboarding) {
      staticContext.unauthorized = true;
    }

    const hasCurrentUser = !!currentUser?.id;
    const restrictedPageWithCurrentUser = !canShow && hasCurrentUser;
    // Banned users are redirected to LandingPage
    const isBannedFromAuthPages = restrictedPageWithCurrentUser && isBanned(currentUser);
    // A confirmed renter hitting an owner-only route is sent to the homepage
    // (they're authenticated, so login/signup redirect would be wrong).
    return blockedByOnboarding ? (
      <NamedRedirect name="OnboardingPage" />
    ) : blockedByOwnerOnly ? (
      <NamedRedirect name="LandingPage" />
    ) : canShow ? (
      <LoadableComponentErrorBoundary>
        <RouteComponent
          params={match.params}
          location={location}
          staticContext={staticContext}
          {...extraProps}
        />
      </LoadableComponentErrorBoundary>
    ) : isBannedFromAuthPages ? (
      <NamedRedirect name="LandingPage" />
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = { staticContext: {} };

RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
  dispatch: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  const { currentUser } = state.user;
  // In-flight role persist (RoleChooser -> saveRoleAndState). Used by the
  // onboarding guard to avoid bouncing a user whose role write hasn't landed yet.
  const saveRoleInProgress = state.OnboardingPage?.saveRoleInProgress === true;
  return { isAuthenticated, logoutInProgress, currentUser, saveRoleInProgress };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress, logLoadDataCalls } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
      routeConfiguration,
      config,
      logLoadDataCalls,
    };

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};

export default withRouter(Routes);
